import React, { useState, useRef, useEffect, useCallback } from "react";
import clsx from "clsx";
import { ChevronDown, Search, X, Check } from "lucide-react";

// Types
export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  group?: string;
}

export interface SelectGroup {
  label: string;
  options: SelectOption[];
}

// Base Select Component
interface SelectProps {
  options: SelectOption[] | SelectGroup[];
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  placeholder?: string;
  label?: string;
  helperText?: string;
  error?: string;
  disabled?: boolean;
  searchable?: boolean;
  multiple?: boolean;
  grouped?: boolean;
  size?: "sm" | "md" | "lg";
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
}

export const Select: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select an option...",
  label,
  helperText,
  error,
  disabled = false,
  searchable = false,
  multiple = false,
  grouped = false,
  size = "md",
  className,
  triggerClassName,
  contentClassName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const triggerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const sizeStyles = {
    sm: "h-8 text-sm px-2",
    md: "h-10 text-base px-3",
    lg: "h-12 text-lg px-4",
  };

  const iconSizes = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6",
  };

  // Flatten options for easier handling
  const flatOptions = React.useMemo(() => {
    if (grouped) {
      return (options as SelectGroup[]).flatMap((group) => group.options);
    }
    return options as SelectOption[];
  }, [options, grouped]);

  // Filter options based on search term
  const filteredOptions = React.useMemo(() => {
    if (!searchable || !searchTerm) {
      return grouped ? (options as SelectGroup[]) : flatOptions;
    }

    const filtered = flatOptions.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    if (grouped) {
      // Group filtered options back
      const groups: SelectGroup[] = [];
      (options as SelectGroup[]).forEach((group) => {
        const groupOptions = group.options.filter((option) =>
          option.label.toLowerCase().includes(searchTerm.toLowerCase()),
        );
        if (groupOptions.length > 0) {
          groups.push({ label: group.label, options: groupOptions });
        }
      });
      return groups;
    }

    return filtered;
  }, [options, searchTerm, searchable, grouped, flatOptions]);

  // Get display value
  const getDisplayValue = () => {
    if (multiple && Array.isArray(value)) {
      if (value.length === 0) return placeholder;
      if (value.length === 1) {
        const option = flatOptions.find((opt) => opt.value === value[0]);
        return option?.label || value[0];
      }
      return `${value.length} selected`;
    }

    if (typeof value === "string") {
      const option = flatOptions.find((opt) => opt.value === value);
      return option?.label || value;
    }

    return placeholder;
  };

  // Check if option is selected
  const isSelected = (optionValue: string) => {
    if (multiple && Array.isArray(value)) {
      return value.includes(optionValue);
    }
    return value === optionValue;
  };

  // Handle option selection
  const handleSelect = (option: SelectOption) => {
    if (option.disabled) return;

    if (multiple) {
      const currentValue = Array.isArray(value) ? value : [];
      const newValue = currentValue.includes(option.value)
        ? currentValue.filter((v) => v !== option.value)
        : [...currentValue, option.value];
      onChange?.(newValue);
    } else {
      onChange?.(option.value);
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    switch (e.key) {
      case "Enter":
      case " ":
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          if (searchable) {
            setTimeout(() => searchRef.current?.focus(), 0);
          }
        } else if (focusedIndex >= 0) {
          const option = getFocusedOption();
          if (option) handleSelect(option);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setSearchTerm("");
        triggerRef.current?.focus();
        break;
      case "ArrowDown":
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setFocusedIndex((prev) => Math.min(prev + 1, getTotalOptions() - 1));
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (isOpen) {
          setFocusedIndex((prev) => Math.max(prev - 1, 0));
        }
        break;
    }
  };

  // Get total number of options
  const getTotalOptions = () => {
    if (grouped) {
      return (filteredOptions as SelectGroup[]).reduce(
        (total, group) => total + group.options.length,
        0,
      );
    }
    return (filteredOptions as SelectOption[]).length;
  };

  // Get focused option
  const getFocusedOption = (): SelectOption | null => {
    if (grouped) {
      let currentIndex = 0;
      for (const group of filteredOptions as SelectGroup[]) {
        if (focusedIndex < currentIndex + group.options.length) {
          return group.options[focusedIndex - currentIndex] || null;
        }
        currentIndex += group.options.length;
      }
    } else {
      return (filteredOptions as SelectOption[])[focusedIndex] || null;
    }
    return null;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node) &&
        contentRef.current &&
        !contentRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Reset focused index when options change
  useEffect(() => {
    setFocusedIndex(-1);
  }, [filteredOptions]);

  // Render option
  const renderOption = (option: SelectOption, index: number) => {
    const selected = isSelected(option.value);
    const focused = index === focusedIndex;

    return (
      <div
        key={option.value}
        className={clsx(
          "px-3 py-2 cursor-pointer flex items-center justify-between transition-colors",
          option.disabled ? "text-gray-500 cursor-not-allowed" : "text-gray-200 hover:bg-gray-800",
          selected && "bg-tracq-bright-blue/20 text-tracq-bright-blue",
          focused && "bg-gray-800",
        )}
        onClick={() => handleSelect(option)}
      >
        <span className="truncate">{option.label}</span>
        {selected && <Check className={clsx("text-tracq-bright-blue", iconSizes[size])} />}
      </div>
    );
  };

  // Render grouped options
  const renderGroupedOptions = () => {
    return (filteredOptions as SelectGroup[]).map((group, groupIndex) => (
      <div key={group.label}>
        <div className="px-3 py-2 text-xs font-medium text-gray-400 bg-gray-800 border-b border-gray-700">
          {group.label}
        </div>
        {group.options.map((option, optionIndex) => {
          const globalIndex =
            getTotalOptions() -
            (filteredOptions as SelectGroup[])
              .slice(groupIndex + 1)
              .reduce((total, g) => total + g.options.length, 0) +
            optionIndex;
          return renderOption(option, globalIndex);
        })}
      </div>
    ));
  };

  // Render flat options
  const renderFlatOptions = () => {
    return (filteredOptions as SelectOption[]).map((option, index) => renderOption(option, index));
  };

  return (
    <div className={clsx("relative", className)}>
      {/* Label */}
      {label && <label className="block text-sm font-medium text-gray-300 mb-2">{label}</label>}

      {/* Trigger */}
      <div
        ref={triggerRef}
        className={clsx(
          "relative flex items-center justify-between",
          "bg-gray-900 border rounded-md transition-colors",
          "text-gray-200",
          sizeStyles[size],
          error
            ? "border-red-500 focus-within:border-red-400"
            : "border-gray-700 hover:border-tracq-bright-blue focus-within:border-tracq-bright-blue",
          disabled && "opacity-50 cursor-not-allowed",
          triggerClassName,
        )}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={handleKeyDown}
        onClick={() => {
          if (!disabled) {
            setIsOpen(!isOpen);
            if (searchable && !isOpen) {
              setTimeout(() => searchRef.current?.focus(), 0);
            }
          }
        }}
      >
        <div className="flex-1 min-w-0">
          {multiple && Array.isArray(value) && value.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {value.slice(0, 3).map((val) => {
                const option = flatOptions.find((opt) => opt.value === val);
                return (
                  <span
                    key={val}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-tracq-bright-blue/20 text-tracq-bright-blue rounded"
                  >
                    {option?.label || val}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const newValue = value.filter((v) => v !== val);
                        onChange?.(newValue);
                      }}
                      className="hover:text-tracq-bright-blue/80"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}
              {value.length > 3 && (
                <span className="text-xs text-gray-400">+{value.length - 3} more</span>
              )}
            </div>
          ) : (
            <span
              className={clsx(
                "truncate",
                (!value || (Array.isArray(value) && value.length === 0)) && "text-gray-400",
              )}
            >
              {getDisplayValue()}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 ml-2">
          {searchable && isOpen && <Search className={clsx("text-gray-400", iconSizes[size])} />}
          <ChevronDown
            className={clsx(
              "text-gray-400 transition-transform",
              isOpen && "rotate-180",
              iconSizes[size],
            )}
          />
        </div>
      </div>

      {/* Dropdown Content */}
      {isOpen && (
        <div
          ref={contentRef}
          className={clsx(
            "absolute z-50 w-full mt-1",
            "bg-gray-900 border border-gray-700 shadow-lg rounded-md",
            "max-h-60 overflow-y-auto",
            contentClassName,
          )}
        >
          {/* Search Input */}
          {searchable && (
            <div className="p-2 border-b border-gray-700">
              <input
                ref={searchRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search options..."
                className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-600 rounded text-gray-200 placeholder-gray-400 focus:outline-none focus:border-tracq-bright-blue"
              />
            </div>
          )}

          {/* Options */}
          <div className="py-1">
            {grouped ? renderGroupedOptions() : renderFlatOptions()}
            {getTotalOptions() === 0 && (
              <div className="px-3 py-2 text-gray-400 text-sm">No options found</div>
            )}
          </div>
        </div>
      )}

      {/* Helper/Error Text */}
      {(helperText || error) && (
        <div className={clsx("mt-1 text-sm", error ? "text-red-400" : "text-gray-400")}>
          {error || helperText}
        </div>
      )}
    </div>
  );
};

// Async Select Component
interface AsyncSelectProps extends Omit<SelectProps, "options"> {
  loadOptions: (searchTerm: string) => Promise<SelectOption[]>;
  loadingText?: string;
  noOptionsText?: string;
}

export const AsyncSelect: React.FC<AsyncSelectProps> = ({
  loadOptions,
  loadingText = "Loading...",
  ...props
}) => {
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm] = useState("");

  const handleSearch = useCallback(
    async (term: string) => {
      setLoading(true);
      try {
        const newOptions = await loadOptions(term);
        setOptions(newOptions);
      } catch (error) {
        console.error("Error loading options:", error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [loadOptions],
  );

  useEffect(() => {
    handleSearch(searchTerm);
  }, [searchTerm, handleSearch]);

  return (
    <Select
      {...props}
      options={options}
      searchable
      {...(loading
        ? { placeholder: loadingText }
        : props.placeholder !== undefined
          ? { placeholder: props.placeholder }
          : {})}
    />
  );
};

// Multi-Select Component
export const MultiSelect: React.FC<Omit<SelectProps, "multiple">> = (props) => {
  return <Select {...props} multiple />;
};

// Searchable Select Component
export const SearchableSelect: React.FC<Omit<SelectProps, "searchable">> = (props) => {
  return <Select {...props} searchable />;
};

// Grouped Select Component
export const GroupedSelect: React.FC<Omit<SelectProps, "grouped">> = (props) => {
  return <Select {...props} grouped />;
};
